import type { PersistedDevicePreferences } from './index.types';
import {
  SELECTED_MICROPHONE,
  SELECTED_RING_DEVICE,
  SELECTED_SPEAKER,
} from 'Constants/localstorage';
import localforage from 'localforage';

export type { PersistedDevicePreferences } from './index.types';

// =============================================================================
// Individual Device Persistence
// =============================================================================

/**
 * Get persisted microphone device ID from localforage.
 * @returns The stored microphone device ID, or null if not set.
 */
export const getPersistedMicrophone = async (): Promise<string | null> => {
  return localforage.getItem<string>(SELECTED_MICROPHONE);
};

/**
 * Set microphone device ID in localforage.
 * @param deviceId - The device ID to persist.
 */
export const setPersistedMicrophone = async (
  deviceId: string
): Promise<void> => {
  await localforage.setItem<string>(SELECTED_MICROPHONE, deviceId);
};

/**
 * Get persisted speaker device ID from localforage.
 * @returns The stored speaker device ID, or null if not set.
 */
export const getPersistedSpeaker = async (): Promise<string | null> => {
  return localforage.getItem<string>(SELECTED_SPEAKER);
};

/**
 * Set speaker device ID in localforage.
 * @param deviceId - The device ID to persist.
 */
export const setPersistedSpeaker = async (deviceId: string): Promise<void> => {
  await localforage.setItem<string>(SELECTED_SPEAKER, deviceId);
};

/**
 * Get persisted ringer device ID from localforage.
 * @returns The stored ringer device ID, or null if not set.
 */
export const getPersistedRinger = async (): Promise<string | null> => {
  return localforage.getItem<string>(SELECTED_RING_DEVICE);
};

/**
 * Set ringer device ID in localforage.
 * @param deviceId - The device ID to persist.
 */
export const setPersistedRinger = async (deviceId: string): Promise<void> => {
  await localforage.setItem<string>(SELECTED_RING_DEVICE, deviceId);
};

// =============================================================================
// Batch Operations
// =============================================================================

/**
 * Load all persisted device preferences in parallel.
 * @returns Object with all device preferences
 */
export const getPersistedDevicePreferences =
  async (): Promise<PersistedDevicePreferences> => {
    const [microphone, speaker, ringer] = await Promise.all([
      getPersistedMicrophone(),
      getPersistedSpeaker(),
      getPersistedRinger(),
    ]);
    return { microphone, speaker, ringer };
  };

// =============================================================================
// Device Selection Helpers
// =============================================================================

/**
 * Resolves the best device ID based on persisted preference and available devices.
 * Falls back to fallbackId or first available device.
 *
 * @param devices - Available media devices
 * @param persistedId - Persisted device ID preference
 * @param fallbackId - Optional fallback device ID
 * @returns The resolved device ID, or null if no devices available
 */
export const resolveDeviceId = (
  devices: MediaDeviceInfo[],
  persistedId: string | null,
  fallbackId?: string | null
): string | null => {
  if (devices.length === 0) return null;

  // Use persisted if device exists
  if (persistedId && devices.some((d) => d.deviceId === persistedId)) {
    return persistedId;
  }

  // Use fallback if provided and exists
  if (fallbackId && devices.some((d) => d.deviceId === fallbackId)) {
    return fallbackId;
  }

  // Default to first device
  return devices[0].deviceId;
};
